import { lazy, Suspense, useRef, useState } from 'react';

import { type CookieConsentOptions, hasConsentCookie } from '@helsenorge/core-cms/cookie-banner/cookie';
import { useSetShowCookieSettings } from '@helsenorge/core-cms/cookie-banner/events';
import { getUseInAppCookieSettings } from '@helsenorge/core-cms/cookie-banner/utils';
import type LanguageLocales from '@helsenorge/core-utils/constants/languages';

const CookieBanner = lazy(() => import('@helsenorge/core-cms/cookie-banner'));
const CookieSettings = lazy(() => import('@helsenorge/core-cms/cookie-banner/cookie-settings'));

interface CookieBannerContainerProps {
  /**
   * Språk for tekster i cookiebanner
   */
  language: LanguageLocales;

  /**
   * Om cookiebanner skal skjules ved oppstart, selv om bruker ikke har gitt samtykke. Brukes for å
   * kunne skjule cookiebanner på sider som skal fortelle mer om cookies og samtykke.
   */
  hideInitially: boolean;
}

const CookieBannerContainer: React.FC<CookieBannerContainerProps> = (props: CookieBannerContainerProps) => {
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(!props.hideInitially && !hasConsentCookie());
  const [showCookieSettings, setShowCookieSettings] = useState<boolean>();
  const handleChangeRef = useRef<(options: CookieConsentOptions) => void>(null);

  useSetShowCookieSettings(detail => {
    setShowCookieSettings(detail.show);
    if (detail.onChange) {
      handleChangeRef.current = detail.onChange;
    }
  });

  // Appen skal alltid bruke innebygd cookiebanner, og ikke cookiebanner på web.
  const useInAppCookieSettings = getUseInAppCookieSettings();

  if (useInAppCookieSettings) {
    return;
  }

  const handleCookieBannerSave = (options: CookieConsentOptions): void => {
    setShowCookieBanner(false);
    if (handleChangeRef.current) {
      handleChangeRef.current(options);
    }
  };

  // Når bruker ikke har gitt noe samtykke, vis cookiebanner
  if (showCookieBanner) {
    return (
      <Suspense fallback={null}>
        <CookieBanner language={props.language} onSave={handleCookieBannerSave} />
      </Suspense>
    );
  }

  const handleCookieSettingsSave = (options: CookieConsentOptions): void => {
    setShowCookieSettings(false);
    if (handleChangeRef.current) {
      handleChangeRef.current(options);
    }
  };

  const handleAbort = (): void => {
    setShowCookieSettings(false);
  };

  // Når bruker vil tilpasse samtykke, vis cookiebanner for tilpasning
  if (showCookieSettings) {
    return (
      <Suspense fallback={null}>
        <CookieSettings language={props.language} onSave={handleCookieSettingsSave} onAbort={handleAbort} />
      </Suspense>
    );
  }
};

export default CookieBannerContainer;
